import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { FormikProps, getIn } from "formik";
import { FC } from "react";

export interface GiftInput {
  gift_name: string;
  gift_type: string;
  gift_image: string;
  gift_description: string;
}

export interface UpdateTargetBadgeFormValues {
  name: string;
  description: string;
  min_points: number;
  max_points: number;
  cash_equivalent: number;
  minimum_cash_equivalent: number;
  badgeId: string;
  gift_basket: GiftInput[];
}

export interface UpdateTargetBadgeFormProps {
  form: FormikProps<UpdateTargetBadgeFormValues>;
}

const GIFT_TYPES = [
  "CASH PRIZE",
  "GIFT ITEM",
  "VOUCHER",
  "TRAVEL",
  "ELECTRONICS",
];

const UpdateTargetBadgeForm: FC<UpdateTargetBadgeFormProps> = ({ form }) => {
  const addGift = () => {
    const updatedGiftBasket = [
      ...form.values.gift_basket,
      {
        gift_name: "",
        gift_type: "",
        gift_image: "",
        gift_description: "",
      },
    ];

    if (updatedGiftBasket.length <= 5) {
      form.setFieldValue("gift_basket", updatedGiftBasket);
    }
  };

  const removeGift = (index: number) => {
    const newGiftBasket = [...form.values.gift_basket];
    newGiftBasket.splice(index, 1);
    form.setFieldValue("gift_basket", newGiftBasket);
  };

  // Helper function to safely get errors and touched values for nested fields
  const getFieldError = (fieldName: string) => {
    const touched = getIn(form.touched, fieldName);
    const error = getIn(form.errors, fieldName);
    return touched && error ? error : null;
  };

  return (
    <div className="space-y-4">
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
        role="alert"
      >
        <div className="font-bold">Badge Update Restrictions</div>
        <p>
          You can only update the cash equivalent value (in Ghana Cedis) and
          gift basket items for this badge.
        </p>
        <p className="mt-2 italic text-sm">
          Badge name, description, and point ranges cannot be modified.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-1">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Badge Name
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={form.values.name}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            disabled
            className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700"
          >
            Description
          </label>
          <textarea
            id="description"
            name="description"
            rows={3}
            className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
            value={form.values.description}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            disabled
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label
            htmlFor="min_points"
            className="block text-sm font-medium text-gray-700"
          >
            Minimum Points
          </label>
          <input
            id="min_points"
            name="min_points"
            type="number"
            value={form.values.min_points}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            disabled
            className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="max_points"
            className="block text-sm font-medium text-gray-700"
          >
            Maximum Points
          </label>
          <input
            id="max_points"
            name="max_points"
            type="number"
            value={form.values.max_points}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            disabled
            className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="minimum_cash_equivalent"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Minimum Cash Equivalent (GHS ₵)
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">₵</span>
            </div>
            <input
              id="minimum_cash_equivalent"
              name="minimum_cash_equivalent"
              type="number"
              placeholder="0.00"
              min={0}
              step="0.01"
              value={form.values.minimum_cash_equivalent}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (isNaN(value) || value >= 0) {
                  form.handleChange(e);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "-" || e.key === "e") {
                  e.preventDefault();
                }
              }}
              onBlur={form.handleBlur}
              className="pl-7 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          <div className="min-h-[1.5rem]">
            {getFieldError("minimum_cash_equivalent") && (
              <p className="mt-1 text-sm text-red-600">
                {getFieldError("minimum_cash_equivalent")}
              </p>
            )}
          </div>
        </div>
        <div>
          <label
            htmlFor="cash_equivalent"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Maximum Cash Equivalent (GHS ₵)
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">₵</span>
            </div>
            <input
              id="cash_equivalent"
              name="cash_equivalent"
              type="number"
              placeholder="0.00"
              min={0}
              step="0.01"
              value={form.values.cash_equivalent}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (isNaN(value) || value >= 0) {
                  form.handleChange(e);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "-" || e.key === "e") {
                  e.preventDefault();
                }
              }}
              onBlur={form.handleBlur}
              className="pl-7 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          <div className="min-h-[1.5rem]">
            {getFieldError("cash_equivalent") && (
              <p className="mt-1 text-sm text-red-600">
                {getFieldError("cash_equivalent")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Gift Basket Section */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-md font-medium text-gray-900">
            Gift Basket (Max 5)
          </h3>
          <button
            type="button"
            onClick={addGift}
            disabled={form.values.gift_basket.length >= 5}
            className={`inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded 
              ${
                form.values.gift_basket.length >= 5
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-primary-100 text-primary-700 hover:bg-primary-200"
              }`}
          >
            <PlusIcon className="h-4 w-4 mr-1" /> Add Gift
          </button>
        </div>

        <div className="space-y-4">
          {form.values.gift_basket.length === 0 && (
            <div className="text-sm text-gray-500 italic">
              No gifts added yet
            </div>
          )}

          {form.values.gift_basket.map((gift, giftIndex) => (
            <div
              key={giftIndex}
              className="p-3 border border-gray-200 rounded-md space-y-3"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Gift #{giftIndex + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeGift(giftIndex)}
                  className="inline-flex items-center p-1 text-red-500 hover:text-red-700"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              <div>
                <label
                  htmlFor={`gift_basket.${giftIndex}.gift_name`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Gift Name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id={`gift_basket.${giftIndex}.gift_name`}
                    name={`gift_basket[${giftIndex}].gift_name`}
                    value={gift.gift_name}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Gift name"
                  />
                  {getFieldError(`gift_basket[${giftIndex}].gift_name`) && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError(`gift_basket[${giftIndex}].gift_name`)}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor={`gift_basket.${giftIndex}.gift_type`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Gift Type
                </label>
                <div className="mt-1">
                  <select
                    id={`gift_basket.${giftIndex}.gift_type`}
                    name={`gift_basket[${giftIndex}].gift_type`}
                    value={gift.gift_type}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  >
                    <option value="">Select a type</option>
                    {GIFT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {getFieldError(`gift_basket[${giftIndex}].gift_type`) && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError(`gift_basket[${giftIndex}].gift_type`)}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor={`gift_basket.${giftIndex}.gift_image`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Gift Image URL
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id={`gift_basket.${giftIndex}.gift_image`}
                    name={`gift_basket[${giftIndex}].gift_image`}
                    value={gift.gift_image}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="https://example.com/image.jpg"
                  />
                  {getFieldError(`gift_basket[${giftIndex}].gift_image`) && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError(`gift_basket[${giftIndex}].gift_image`)}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor={`gift_basket.${giftIndex}.gift_description`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Gift Description
                </label>
                <div className="mt-1">
                  <textarea
                    id={`gift_basket.${giftIndex}.gift_description`}
                    name={`gift_basket[${giftIndex}].gift_description`}
                    value={gift.gift_description}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    rows={2}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Description of the gift"
                  />
                  {getFieldError(
                    `gift_basket[${giftIndex}].gift_description`
                  ) && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError(
                        `gift_basket[${giftIndex}].gift_description`
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UpdateTargetBadgeForm;
